import React, { createContext, useCallback, useContext, useState } from 'react';

import api from '../services/api';

interface signInCredentials {
  email: string;
  password: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  company: string;
  cnpj: string;
  agency: boolean;
}

interface AuthContextData {
  user: User;
  signIn(credentials: signInCredentials): Promise<void>;
  signOut(): void;
}

interface AuthState {
  token: string;
  user: User;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const user = localStorage.getItem('@PublicidadeLegal:user');
    const token = localStorage.getItem('@PublicidadeLegal:token');

    if (token && user) {
      api.defaults.headers.authorization = `Bearer ${token}`;

      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }) => {
    const response = await api.post('login', {
      email,
      password,
    });

    if (response) {
      const { user, token } = response.data;
      localStorage.setItem('@PublicidadeLegal:user', JSON.stringify(user));
      localStorage.setItem('@PublicidadeLegal:token', token);

      api.defaults.headers.authorization = `Bearer ${token}`;

      setData({ token, user });
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@PublicidadeLegal:user');
    localStorage.removeItem('@PublicidadeLegal:token');

    api.defaults.headers.authorization = ``;

    setData({} as AuthState);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
